import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, viewChild } from '@angular/core'
import { rxResource, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'
import { map } from 'rxjs'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DocumentPreviewService } from './services/document-preview.service'

/**
 * Shows the document preview of a certificate in its own tab, outside the app shell.
 * The HTML is rendered in a sandboxed iframe so neither its styles nor its scripts reach the app.
 */
@Component({
  selector: 'albert-certificate-preview',
  imports: [ProgressSpinnerModule],
  template: `
    @if (preview.isLoading()) {
      <div class="flex h-dvh items-center justify-center">
        <p-progress-spinner data-test="certificate-preview-loading"/>
      </div>
    } @else if (preview.error()) {
      <div class="flex h-dvh items-center justify-center text-red-600"
           data-test="certificate-preview-error"
      >
        The preview could not be loaded.
      </div>
    } @else {
      <iframe #frame
              class="block h-dvh w-full border-0"
              data-test="certificate-preview-frame"
              sandbox=""
              title="Certificate Preview"
      ></iframe>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificatePreviewComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly documentPreviewService = inject(DocumentPreviewService)

  private readonly uuid = toSignal(this.route.paramMap.pipe(map(params => params.get('uuid') ?? undefined)))

  protected readonly preview = rxResource({
    params: () => this.uuid(),
    stream: ({ params }) => this.documentPreviewService.getPreviewHtml(params)
  })

  private readonly frame = viewChild<ElementRef<HTMLIFrameElement>>('frame')

  constructor() {
    // Set directly rather than bound: a [srcdoc] binding runs through Angular's sanitizer, which strips the
    // document's <head> and <style>. The empty sandbox is what keeps the HTML from running scripts or reaching the app.
    effect(() => {
      const frame = this.frame()
      if (frame !== undefined && this.preview.hasValue()) {
        frame.nativeElement.srcdoc = this.preview.value()
      }
    })
  }
}
